/**
 * Platform data: lookups, form schemas, navigation modules, processes.
 *
 * WHY THIS FILE EXISTS AT ALL, rather than each screen fetching what it wants:
 *
 * NO DROPDOWN IN THIS APPLICATION IS HARDCODED. Not one `<option>` list, not one TS enum
 * rendered as choices. Every set of choices comes from `LookupEntries` via
 * `/api/platform/lookups/:key`. The test for whether this is done right: an admin adds
 * "Revenue Share" to the project fee-type list, and it appears in the project form
 * without a deploy. A hardcoded enum fails that test silently, which is why it has to be
 * a shared hook rather than a convention people remember.
 *
 * Lookups change rarely and are read constantly, so they are cached hard (30 minutes) and
 * invalidated explicitly by the lookup admin screens.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@frontend/api/client';
import { humanise } from '@frontend/lib/format';
import type {
  CustomField,
  FieldDependency,
  FormFieldDefinition,
  FormSchema,
  FormSection,
  LookupEntry,
  LookupList,
  ModuleConfig,
} from '@shared/types';

const LOOKUP_STALE_MS = 30 * 60_000;

export interface LookupOption {
  value: string;
  label: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  isDefault?: boolean;
  metadata?: Record<string, unknown>;
}

export interface LookupListResponse {
  list: LookupList;
  entries: LookupEntry[];
}

/**
 * One lookup list plus its counts, FLATTENED.
 *
 * `GET /api/platform/lookups` answers `{ lists: [{ list, entryCount, activeCount }] }` —
 * the row is a WRAPPER around the list, not the list. This hook used to declare the
 * response as `{ lists: LookupList[] }`, which `api.get<T>` accepted because it is an
 * unchecked cast, and `/admin/lookups` then read `list.key.split('_')` on the wrapper and
 * threw `Cannot read properties of undefined (reading 'split')` into the error boundary.
 * The whole configurability screen was down.
 *
 * `readList` did not catch it because `readList` only normalises WHICH KEY the rows sit
 * under; it makes no claim about the shape of a row. This was a row-shape mismatch, and the
 * fix is to unwrap here, once, so no screen ever sees the wrapper.
 */
export interface LookupListSummary extends LookupList {
  entryCount: number;
  activeCount: number;
}

interface LookupListsPayload {
  lists?: { list?: LookupList; entryCount?: number; activeCount?: number }[];
}

export function useLookupLists(): UseQueryResult<{ lists: LookupListSummary[] }> {
  return useQuery({
    queryKey: ['platform', 'lookups'],
    queryFn: async () => {
      const body = await api.get<LookupListsPayload>('/platform/lookups');
      const rows = Array.isArray(body.lists) ? body.lists : [];
      const lists: LookupListSummary[] = [];
      for (const row of rows) {
        // Tolerate BOTH shapes: the wrapper the server sends today, and the flat row it
        // would send if the envelope is ever standardised. Neither produces `undefined.key`.
        const list = row.list ?? (row as unknown as LookupList);
        if (typeof list?.key !== 'string') continue;
        lists.push({
          ...list,
          entryCount: row.entryCount ?? 0,
          activeCount: row.activeCount ?? 0,
        });
      }
      return { lists };
    },
    staleTime: LOOKUP_STALE_MS,
  });
}

/**
 * The options for one lookup list.
 *
 * `enabled` lets a screen defer the fetch until the key is known (a dependent dropdown,
 * for instance) without breaking the rules of hooks.
 */
export function useLookup(key: string | null, options?: { includeInactive?: boolean }): UseQueryResult<LookupListResponse> {
  return useQuery({
    queryKey: ['platform', 'lookup', key, options?.includeInactive === true],
    queryFn: () =>
      api.get<LookupListResponse>(`/platform/lookups/${key ?? ''}`, {
        includeInactive: options?.includeInactive === true ? 'true' : undefined,
      }),
    enabled: key !== null && key !== '',
    staleTime: LOOKUP_STALE_MS,
  });
}

/** Lookup entries flattened to display options, active only, in the admin's sort order. */
export function useLookupOptions(key: string | null): {
  options: LookupOption[];
  isLoading: boolean;
  error: unknown;
} {
  const query = useLookup(key);
  const options = (query.data?.entries ?? [])
    .filter((entry) => entry.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map<LookupOption>((entry) => ({
      value: entry.value,
      label: entry.label,
      description: entry.description,
      color: entry.color,
      icon: entry.icon,
      isDefault: entry.isDefault,
      metadata: (entry.metadata ?? {}) as Record<string, unknown>,
    }));
  return { options, isLoading: query.isLoading, error: query.error };
}

/**
 * Resolves a STORED VALUE to the label the admin gave it.
 *
 * The rule this enforces is CLAUDE.md #3: the entries carry `T&M`, `FTE`, `1099 Contractor`
 * and the screens were rendering `humanise('TM')` → "Tm", `humanise('FTE')` → "Fte". Those
 * are not typos in the data, they are the code refusing to read the data. An admin who
 * relabels "Fixed Bid" to "Fixed Price" must see it everywhere the value appears, not only
 * in the dropdown they edited.
 *
 * `humanise` remains the fallback for a value with no matching entry — a legacy row, or a
 * value whose entry was deleted — because showing the raw `WORK_IN_PROGRESS` is worse.
 */
export function useLookupLabel(key: string | null): (value: string | null | undefined) => string {
  const { options } = useLookupOptions(key);
  const byValue = new Map(options.map((option) => [option.value, option.label]));
  return (value) => {
    if (value === null || value === undefined || value === '') return '';
    return byValue.get(value) ?? humanise(value);
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Form schemas
// ─────────────────────────────────────────────────────────────────────────────

export interface AssembledFormSection extends FormSection {
  fields: FormFieldDefinition[];
}

export interface AssembledForm {
  schema: FormSchema;
  sections: AssembledFormSection[];
  dependencies: FieldDependency[];
  customFields: CustomField[];
}

/**
 * The form definition for an entity, as configured by the admin.
 *
 * Screens must NOT hardcode a field list for entities the schema covers — the admin's
 * relabelling, reordering, hiding and requiring has to take effect, and a hardcoded list
 * ignores all four.
 */
export function useFormSchema(entityType: string | null, variant?: string): UseQueryResult<AssembledForm> {
  return useQuery({
    queryKey: ['platform', 'form', entityType, variant ?? 'default'],
    queryFn: () =>
      api.get<AssembledForm>(`/platform/forms/${entityType ?? ''}`, {
        variant,
      }),
    enabled: entityType !== null && entityType !== '',
    staleTime: 10 * 60_000,
  });
}

export function useFormList(): UseQueryResult<{ forms: FormSchema[] }> {
  return useQuery({
    queryKey: ['platform', 'forms'],
    queryFn: () => api.get<{ forms: FormSchema[] }>('/platform/forms'),
    staleTime: 10 * 60_000,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Modules and navigation
// ─────────────────────────────────────────────────────────────────────────────

export interface NavigationItem {
  moduleKey: string;
  label: string;
  icon: string | null;
  sortOrder: number;
}

/**
 * Which modules the ADMIN has enabled. Combined with the user's permissions in
 * `Sidebar` — a nav item needs both. A disabled module is hidden from everyone including
 * super-admins, because "disabled" is a deployment decision, not an access decision.
 */
export function useNavigation(): UseQueryResult<{ items: NavigationItem[] }> {
  return useQuery({
    queryKey: ['platform', 'navigation'],
    queryFn: () => api.get<{ items: NavigationItem[] }>('/platform/navigation'),
    staleTime: 10 * 60_000,
    // Navigation must not hard-fail the shell: a user without
    // `platform.navigation.read` still needs a usable sidebar.
    retry: false,
  });
}

export function useModules(): UseQueryResult<{ modules: ModuleConfig[] }> {
  return useQuery({
    queryKey: ['platform', 'modules'],
    queryFn: () => api.get<{ modules: ModuleConfig[] }>('/platform/modules'),
    staleTime: 5 * 60_000,
  });
}

export interface ProcessRegistration {
  key: string;
  label?: string;
  module?: string;
  entityType?: string;
  policy?: string;
  description?: string;
}

export function useProcesses(): UseQueryResult<{ processes: ProcessRegistration[] }> {
  return useQuery({
    queryKey: ['platform', 'processes'],
    queryFn: () => api.get<{ processes: ProcessRegistration[] }>('/platform/processes'),
    staleTime: 10 * 60_000,
  });
}
